/* Public product images only. Customer photos and AI previews are never admitted. */
(() => {
  function createCache({urls, base, storage, fetchImage, createObjectURL, onProgress=()=>{}, yieldWork=async()=>{}}) {
    const normalize = value => new URL(value, base).href;
    const identity = value => {
      const url = new URL(value, base);
      url.searchParams.delete('width');
      url.searchParams.sort();
      return url.href;
    };
    const manifest = [...new Set(urls.filter(Boolean).map(normalize))];
    const candidates = new Map();
    for (const url of manifest) {
      const key = identity(url);
      if (!candidates.has(key)) candidates.set(key, []);
      candidates.get(key).push(url);
    }
    const objects = new Map(), inFlight = new Map();
    let downloadsEnabled=true;
    const cacheReady = storage.open('hillsdale-public-images-v1');
    const state = {total:manifest.length,cached:0,failed:0,complete:false};
    function canonical(value) {
      try {
        const options = candidates.get(identity(value));
        if (!options) return null;
        const requested = Number(new URL(value,base).searchParams.get('width')) || 900;
        return options.find(url => Number(new URL(url).searchParams.get('width')) === requested) ||
          (requested<=150 && options.find(url => Number(new URL(url).searchParams.get('width'))===150)) ||
          options.find(url => Number(new URL(url).searchParams.get('width')) === 900) || options[0];
      } catch { return null; }
    }
    async function responseFor(url) {
      if (inFlight.has(url)) return (await inFlight.get(url)).clone();
      const work = (async () => {
        const cache = await cacheReady;
        const saved = await cache.match(url);
        if (saved) return saved;
        if(!downloadsEnabled)throw new Error('Image downloads disabled for verification');
        const response = await fetchImage(url, {mode:'cors',credentials:'omit'});
        if (!response.ok || !response.headers.get('content-type')?.startsWith('image/'))
          throw new Error('Image unavailable');
        await cache.put(url,response.clone());
        return response;
      })();
      inFlight.set(url,work);
      try { return (await work).clone(); } finally { inFlight.delete(url); }
    }
    async function resolve(value) {
      const url = canonical(value);
      if (!url) return value;
      if (objects.has(url)) return objects.get(url);
      try {
        const response = await responseFor(url);
        // Another renderer may have completed while this response was read.
        const blob = await response.blob();
        if (!objects.has(url)) objects.set(url,createObjectURL(blob));
        return objects.get(url);
      } catch { return value; }
    }
    async function warm() {
      state.cached=0;state.failed=0;state.complete=false;
      let next=0;
      await Promise.all(Array.from({length:4},async()=>{
        while(next<manifest.length) {
          const url=manifest[next++];
          try { await responseFor(url);state.cached++; } catch {state.failed++;}
          onProgress({...state});
          await yieldWork();
        }
      }));
      state.complete=state.cached===state.total;
      onProgress({...state});
      // Keep the old assets until every new asset is safely stored.
      if(state.complete) {
        const cache=await cacheReady, current=new Set(manifest);
        for(const request of await cache.keys()) if(!current.has(request.url)) await cache.delete(request);
      }
      return {...state};
    }
    async function verifyStored(decode, revoke) {
      const cache=await cacheReady;
      let passed=0,failed=0,next=0;
      await Promise.all(Array.from({length:4},async()=>{
        while(next<manifest.length) {
          const original=manifest[next++];let objectUrl;
          try {
            // Deliberately no network fallback in this verification path.
            const response=await cache.match(original);
            if(!response)throw new Error('Missing cached image');
            objectUrl=createObjectURL(await response.blob());
            await decode(objectUrl);passed++;
          } catch {failed++;}
          finally {if(objectUrl)revoke(objectUrl);}
        }
      }));
      return {passed,failed,total:manifest.length};
    }
    return {resolve,warm,state,canonical,verifyStored,
      disableDownloads:()=>{downloadsEnabled=false;}};
  }
  // Factory remains testable without a browser or a real customer's session.
  if (typeof module !== 'undefined') module.exports={createCache};
  if (typeof document === 'undefined') return;
  const root=document.querySelector('#instame-in-store-ipad');
  if(!root || root.dataset.orderMode==='phone' || !window.caches) return;
  const manifest=document.querySelector('[data-in-store-image-manifest]');
  if(!manifest) return;
  let urls;
  try {urls=JSON.parse(manifest.textContent);} catch {return;}
  for(const [key,value] of Object.entries(root.dataset))
    if(/(example|welcomePet|welcomeProduct|tapPhoto|phoneChoose|phoneSend|qr)Url$/.test(key)) urls.push(value);
  root.querySelectorAll('.in-store-ipad__logo img').forEach(img=>urls.push(img.src));
  const cache=createCache({urls,base:location.href,storage:window.caches,
    fetchImage:window.fetch.bind(window),createObjectURL:URL.createObjectURL.bind(URL),
    // Let taps, layout and image decoding run between background cache writes.
    yieldWork:()=>new Promise(resolve=>setTimeout(resolve,16)),
    onProgress:state=>{
      if (!state.complete && (state.cached + state.failed) % 20 !== 0) return;
      root.dataset.imageCacheTotal=state.total;
      root.dataset.imageCacheCached=state.cached;
      root.dataset.imageCacheFailed=state.failed;
      root.dataset.imageCacheComplete=state.complete;
    }});
  window.InstaMePublicImageCache=cache;
  const observers=new WeakSet();
  const pending=new WeakMap();
  async function render(img) {
    if (img.tagName !== 'IMG') return;
    const original=img.getAttribute('src');
    if(!original || !cache.canonical(original) || pending.get(img)===original) return;
    pending.set(img,original);
    const cached=await cache.resolve(original);
    pending.delete(img);
    if(cached===original || img.getAttribute('src')!==original) return;
    img.removeAttribute('srcset');img.removeAttribute('sizes');
    img.src=cached;
  }
  const visibleImages=typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(entries=>{
    for(const entry of entries) if(entry.isIntersecting){visibleImages.unobserve(entry.target);void render(entry.target);}
  },{rootMargin:'300px'});
  function watchImage(img) {
    if(visibleImages)visibleImages.observe(img);else void render(img);
  }
  async function renderSwatch(element) {
    if(!element.matches?.('.swatch'))return;
    const original=element.style.getPropertyValue('--swatch-background').trim();
    const match=/^url\(['"]?([^'"]+)['"]?\)$/.exec(original);
    if(!match || !cache.canonical(match[1]))return;
    const cached=await cache.resolve(match[1]);
    if(cached===match[1] || element.style.getPropertyValue('--swatch-background').trim()!==original)return;
    element.style.setProperty('--swatch-background',`url("${cached}")`);
  }
  function scan(container) {
    if(container.matches?.('img')) watchImage(container);
    container.querySelectorAll?.('img').forEach(watchImage);
    renderSwatch(container);
    container.querySelectorAll?.('.swatch').forEach(renderSwatch);
    // Only the swatch overflow component owns images in a shadow root.
    container.querySelectorAll?.('overflow-list').forEach(node=>{if(node.shadowRoot) observe(node.shadowRoot);});
  }
  function observe(container) {
    if(observers.has(container))return;
    observers.add(container);scan(container);
    new MutationObserver(records=>{
      for(const record of records) {
        if(record.type==='attributes'){
          if(record.target.tagName==='IMG')watchImage(record.target);
          else if(record.target.matches?.('.swatch'))renderSwatch(record.target);
        }
        else record.addedNodes.forEach(scan);
      }
    }).observe(container,{childList:true,subtree:true,attributes:true,attributeFilter:['src','srcset','style']});
  }
  // Native preview dialogs mount outside the station root. The same strict
  // public manifest still excludes every uploaded or generated pet image.
  observe(document.body);
  // Public cache survives Start Over and reload. Missing/evicted entries refill.
  let warming=null;
  const warm=()=>warming || (warming=cache.warm().then(result=>{scan(root);return result;}).finally(()=>{warming=null;}));
  // Visible images load first. Continue filling the persistent catalogue cache
  // after the initial screen has painted, rather than competing with startup.
  if(window.requestIdleCallback)window.requestIdleCallback(()=>{void warm();},{timeout:3000});
  else setTimeout(()=>{void warm();},1000);
  window.addEventListener('online',()=>{void warm();});
  const verifyButton=document.querySelector('[data-cache-verify]');
  verifyButton?.addEventListener('click',async()=>{
    verifyButton.disabled=true;
    const output=document.querySelector('[data-cache-verification]');
    output.textContent='Checking stored images with image downloads disabled…';
    try {
      const result=await cache.verifyStored(async src=>{
        const image=new Image();image.src=src;await image.decode();
      },URL.revokeObjectURL.bind(URL));
      output.textContent=`Stored image decode: ${result.passed}/${result.total} passed; ${result.failed} failed. No image downloads used.`;
    } catch {output.textContent='Stored image verification failed.';}
    finally {verifyButton.disabled=false;}
  });
})();
